import React from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles.css';

export default function ServicesPage() {
  return (
    <main className="services-page">
      <Helmet>
		  <title>EPUB Conversion, Metadata Services, ONIX, and Backlist Recovery | HermesReads</title>
		  <meta
			name="description"
			content="HermesReads helps publishers, authors, archives, and catalog owners convert old books, manuscripts, scans, PDFs, OCR text, and existing files into clean EPUBs, print-ready bodies, enriched metadata, ONIX-ready records, and distributor-ready publishing material."
		  />
		  <meta
			name="keywords"
			content="EPUB conversion, ebook conversion, ebook formatting, metadata services, ONIX metadata, book metadata enrichment, backlist recovery, publisher backlist digitization, OCR cleanup, print to EPUB, PDF to EPUB, PublishDrive preparation, KDP formatting, IngramSpark preparation, public domain publishing"
		  />
		  <meta property="og:title" content="EPUB Conversion, Metadata Services, and Backlist Recovery | HermesReads" />
		  <meta
			property="og:description"
			content="Convert older books, PDFs, scans, OCR text, DOCX files, and messy catalog material into clean EPUBs, stronger metadata, ONIX-ready records, and distributor-ready publishing packages."
		  />
		  <meta property="og:type" content="website" />
		  <meta property="og:url" content="https://www.hermesreads.com/services" />
		  <meta property="og:image" content="https://www.hermesreads.com/android-chrome-512x512.png" />
		  <meta name="twitter:card" content="summary_large_image" />
		  <meta name="twitter:title" content="EPUB Conversion, Metadata Services, and Backlist Recovery | HermesReads" />
		  <meta
			name="twitter:description"
			content="Book-production services for EPUB conversion, OCR cleanup, metadata enrichment, ONIX preparation, and publisher backlist recovery."
		  />
		  <meta name="twitter:image" content="https://www.hermesreads.com/android-chrome-512x512.png" />
		  <link rel="canonical" href="https://www.hermesreads.com/services" />

		  <script type="application/ld+json">
			{JSON.stringify({
			  "@context": "https://schema.org",
			  "@type": "Service",
			  "name": "EPUB conversion and metadata services",
			  "description": "HermesReads provides EPUB conversion, ebook formatting, OCR cleanup, metadata enrichment, ONIX preparation, distributor upload preparation, and publisher backlist recovery.",
			  "provider": {
				"@type": "Organization",
				"name": "HermesReads",
				"url": "https://www.hermesreads.com/"
			  },
			  "areaServed": "Worldwide",
			  "serviceType": [
				"EPUB conversion",
				"ebook formatting",
				"OCR cleanup",
				"metadata enrichment",
				"ONIX metadata preparation",
				"backlist recovery",
				"catalog cleanup",
				"print-on-demand preparation"
			  ],
			  "url": "https://www.hermesreads.com/services"
			})}
		  </script>
		</Helmet>

      <section className="services-hero">
        <p className="services-kicker">HermesReads Services</p>

        <h1>Turn old books and messy files into clean EPUBs, print-ready bodies, and distributor-ready metadata.</h1>

        <p className="services-lead">
          HermesReads helps publishers, authors, and catalog owners recover value from books they
          already have: older backlist titles, manuscripts, scans, OCR text, PDFs, public domain
          works, and existing book files that need to become usable digital editions.
        </p>

        <div className="services-actions">

          <a href="mailto:noah@hermesreads.com" className="services-secondary-button">
            Email HermesReads
          </a>
        </div>
      </section>

      <section className="services-problems" aria-labelledby="services-problems-heading">
        <h2 id="services-problems-heading">Problems this solves</h2>

        <div className="services-problem-list">
          <p>Your EPUB coverage is incomplete.</p>
          <p>Your older books only exist as print PDFs, scans, DOCX files, or raw text.</p>
          <p>Your OCR text has broken spacing, bad chapters, damaged notes, or inconsistent structure.</p>
          <p>Your metadata is too thin for search, discovery, classification, or distributor uploads.</p>
          <p>Your categories, keywords, author fields, ISBN fields, descriptions, or subtitles are inconsistent.</p>
          <p>Your catalog is too large to clean manually one book at a time.</p>
        </div>
      </section>

      <section className="services-positioning">
        <h2>Backlist recovery, not generic file conversion</h2>

        <p>
          The main value is not simply producing an EPUB file. The point is to take books that are
          difficult to sell, search, distribute, classify, or read digitally, and turn them into
          practical publishing assets.
        </p>

        <p>
          For publishers, this is mainly a backlist recovery service: old books, weak metadata,
          missing EPUBs, and messy files turned into usable digital editions. For authors and smaller
          projects, it is a way to get from scattered files to a cleaner publishing package.
        </p>
      </section>

      <section className="services-grid" aria-label="Services">
        <article className="service-card">
          <h2>Backlist EPUB conversion</h2>
          <p>
            Conversion of older books and publisher backlist material into clean EPUB files that can
            be read, distributed, and reused across platforms.
          </p>
          <ul>
            <li>DOCX, HTML, XHTML, OCR text, and prepared source files</li>
            <li>PDF-derived text cleanup where possible</li>
            <li>Chapter splitting and book structure</li>
            <li>Navigation, table of contents, and internal linking</li>
          </ul>
        </article>

        <article className="service-card">
          <h2>Metadata repair and enrichment</h2>
          <p>
            Metadata is what makes a book findable, classifiable, sellable, and usable across
            platforms. I clean and strengthen the fields that actually matter.
          </p>
          <ul>
            <li>Titles, subtitles, authors, years, languages, and ISBN fields</li>
            <li>Descriptions and back-cover copy</li>
            <li>Reader-search-oriented keywords</li>
            <li>Category, subject, and classification refinement</li>
          </ul>
        </article>

        <article className="service-card">
          <h2>Distributor upload preparation</h2>
          <p>
            Preparation of structured publishing material for platforms and distributors where weak
            metadata or inconsistent files slow down publication.
          </p>
          <ul>
            <li>PublishDrive-oriented metadata preparation</li>
            <li>Amazon KDP, Draft2Digital, and IngramSpark-style field cleanup</li>
            <li>ONIX-ready metadata structuring</li>
            <li>BISAC, Thema, Dewey, and LCC-oriented classification support</li>
          </ul>
        </article>

        <article className="service-card">
          <h2>Public domain and archive editions</h2>
          <p>
            Support for turning older public domain texts, archive material, forgotten works, and
            translation projects into publishable digital editions.
          </p>
          <ul>
            <li>OCR cleanup and structure repair</li>
            <li>Footnote and endnote handling</li>
            <li>Chapter, section, and title-page organization</li>
            <li>Catalog and series consistency</li>
          </ul>
        </article>

        <article className="service-card">
          <h2>Print and ebook coordination</h2>
          <p>
            Alignment between ebook files, print interiors, covers, ISBNs, descriptions, and metadata
            so the same title is consistent across formats.
          </p>
          <ul>
            <li>EPUB, print-ready interior, and metadata consistency</li>
            <li>ISBN field organization</li>
            <li>Title, subtitle, author, and description alignment</li>
            <li>Pre-upload quality checks</li>
          </ul>
        </article>

        <article className="service-card">
          <h2>Large-catalog processing</h2>
          <p>
            For publishers, archives, or catalog owners with many files, I can help build or adapt
            repeatable workflows instead of treating each book as a separate manual job.
          </p>
          <ul>
            <li>Batch conversion workflows</li>
            <li>Metadata JSON and spreadsheet cleanup</li>
            <li>Automated classification support</li>
            <li>Large-scale quality-control routines</li>
          </ul>
        </article>
      </section>

      <section className="services-files">
        <h2>What I can work with</h2>

        <div className="services-file-grid">
          <span>DOCX manuscripts</span>
          <span>HTML / XHTML</span>
          <span>Existing EPUBs</span>
          <span>OCR text</span>
          <span>PDF-derived text</span>
          <span>ONIX, JSON, DC metadata</span>
          <span>CSV / spreadsheet metadata</span>
          <span>Public domain source files</span>
          <span>Publisher backlist material</span>
          <span>Translation projects</span>
          <span>Scanned-book workflows</span>
          <span>Print and ebook packages</span>
        </div>
      </section>

      <section className="services-deliverables">
        <h2>Typical deliverables</h2>

        <div className="services-deliverables-grid">
          <div>
            <h3>Clean EPUB file</h3>
            <p>A structured EPUB with chapters, navigation, internal links, and readable formatting.</p>
          </div>

          <div>
            <h3>Print-ready body file</h3>
            <p>A clean interior file prepared for print-on-demand workflows where applicable.</p>
          </div>

          <div>
            <h3>Improved metadata</h3>
            <p>Cleaner title, author, subtitle, year, language, ISBN, category, and subject fields.</p>
          </div>

          <div>
            <h3>Description and keywords</h3>
            <p>Search-oriented descriptions, back-cover copy, and keywords based on reader intent.</p>
          </div>

          <div>
            <h3>Distributor-ready package</h3>
            <p>Material prepared for upload workflows, including ONIX-style or platform-specific fields.</p>
          </div>

          <div>
            <h3>Quality-control notes</h3>
            <p>A clear list of remaining issues, file assumptions, and things that may need manual review.</p>
          </div>
        </div>
      </section>

      <section className="services-understanding">
        <h2>Understanding the Work</h2>

        <div className="services-understanding-grid">
          <article className="service-card">
            <h2>Professional EPUBs</h2>

            <p>
              Many EPUBs technically open while still containing structural problems that create
              issues for readers, distributors, and future maintenance.
            </p>

            <p>
              Professional EPUB preparation involves navigation structure, chapter hierarchy,
              internal links, footnotes, semantic markup, validation, and compatibility across
              reading systems.
            </p>
          </article>

          <article className="service-card">
            <h2>Metadata</h2>

            <p>
              Metadata determines how books are found, classified, organized, and distributed.
            </p>

            <p>
              Titles, subtitles, descriptions, keywords, ISBNs, contributors, dates, and categories
              all affect discoverability, catalog quality, and distributor handling.
            </p>
          </article>

          <article className="service-card">
            <h2>ONIX</h2>

            <p>
              ONIX is the publishing industry's standard format for exchanging book information.
            </p>

            <p>
              It allows publishers, distributors, retailers, and catalog systems to exchange
              structured metadata including descriptions, categories, contributors, pricing,
              publication dates, rights, and keywords.
            </p>
          </article>

          <article className="service-card">
            <h2>Classification Systems</h2>

            <p>
              Category systems such as BISAC, Thema, Dewey, and Library of Congress Classification
              help books reach the right audiences and catalog environments.
            </p>

            <p>
              Good classification improves discovery, retailer placement, library handling, and
              long-term catalog organization.
            </p>
          </article>

          <article className="service-card">
            <h2>Public Domain Publishing</h2>

            <p>
              Public domain publishing involves much more than scanning a book and exporting a file.
            </p>

            <p>
              OCR quality, structure, metadata, notes, formatting, edition consistency, and platform
              preparation all influence the final result.
            </p>
          </article>

          <article className="service-card">
            <h2>Large Catalogs</h2>

            <p>
              The challenges of processing ten books are very different from the challenges of
              processing hundreds or thousands.
            </p>

            <p>
              Larger projects often benefit from workflows, automation, metadata pipelines,
              repeatable quality-control systems, and consistent classification rules.
            </p>
          </article>
        </div>
      </section>

      <section className="services-process">
        <h2>How the work usually happens</h2>

        <div className="services-process-grid">
          <div>
            <span>01</span>
            <h3>Review the material</h3>
            <p>
              I look at the source material, metadata, file structure, and intended platform.
            </p>
          </div>

          <div>
            <span>02</span>
            <h3>Define the useful output</h3>
            <p>
              We decide whether the goal is EPUB conversion, metadata cleanup, distributor
              preparation, print coordination, or a larger workflow.
            </p>
          </div>

          <div>
            <span>03</span>
            <h3>Clean and structure</h3>
            <p>
              The files and metadata are cleaned, normalized, formatted, classified, and checked for
              obvious structural issues.
            </p>
          </div>

          <div>
            <span>04</span>
            <h3>Deliver usable material</h3>
            <p>
              The final result is practical publishing material: files, metadata, notes, and
              upload-ready structure where applicable.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}